import { Suspense, lazy } from 'react';
import { useApp } from '../../../../hooks/AppProvider';
import { useTimerProps } from '../../../../hooks/useTimerProps';
import { useTranslation } from 'react-i18next';
import { Sheet, SheetClose } from '@fastnorth/kraken/components/sheet';
import { MessageCircleQuestion, X } from 'lucide-react';
import { CardSkeleton } from '@fastnorth/kraken/components/card';
import { Flex, Div } from '@fastnorth/kraken/components/layout';
import TimerDetails from '../../../timer-details/TimerDetails';
import { Button } from '@fastnorth/kraken/components/button';
import { H2 } from '@fastnorth/kraken/components/typography';
import { useResponsive } from '@fastnorth/kraken/components/page-layout';

const ContactTimerForm = lazy(() => import('./ContactTimerForm'));

const ContactTimerSheet = (): React.JSX.Element | null => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'karwa.components.contact_timer_sheet',
  });
  const [{ event }] = useApp();
  const { timerProps } = useTimerProps(event || null);
  const { isMobile } = useResponsive();

  const onFormEngage = (): void => {
    document.body.style.pointerEvents = '';
  };

  // Don't render if there are no timer props or if contact form is not enabled
  if (!timerProps || !timerProps.listingSettings?.hasTimerContactForm) {
    return null;
  }

  return (
    <Sheet
      trigger={{
        variant: 'outline',
        children: (
          <>
            <MessageCircleQuestion
              role={'presentation'}
              size={16}
              strokeWidth={2}
              className={'mr-4'}
            />
            {t('trigger_label')}
          </>
        ),
        // @ts-expect-error valid data attribute
        'data-dd-action-name': 'btn-contact_timer-page_footer',
      }}
      sheetTitle={{ text: t('title') }}
      sheetTitleVisible={false}
      width={'full'}
      // explicitly setting this because recaptcha refuses to work without it
      modal={true}
      sheetContentProps={{
        onInteractOutside: (e) => {
          e.preventDefault(); // needed for recaptcha to work
        },
        side: 'bottom',
      }}>
      <Suspense fallback={<CardSkeleton footer header />}>
        <Flex className={'flex-col md:flex-row relative'}>
          <Div className={'absolute top-5 right-6'}>
            <SheetClose asChild>
              <Button variant={'ghost'} aria-label={'Close'} size={'icon'}>
                <X size={16} strokeWidth={2} className={'mx-auto'} />
              </Button>
            </SheetClose>
          </Div>

          {!isMobile && (
            <Div
              className={'bg-primary text-primary-foreground p-[48px] w-1/2 md:max-w-[565px]'}
              data-testid={'timer-details-desktop'}>
              <TimerDetails timingProvider={timerProps.timingProvider} inlineLogo={false} />
            </Div>
          )}

          <Div className={'w-full px-7 pt-7 h-[100vh] md:overflow-y-scroll'}>
            <Flex className={'flex-col md:max-w-[565px] pb-[48px] justify-self-center'}>
              <Div className={'max-w-[90%]'}>
                <H2 size={'lg'} spacing={'0 0 6'}>
                  {t('title')}
                </H2>
              </Div>
              {isMobile && (
                <Div
                  className={'bg-primary text-primary-foreground p-7 mx-[-24px] mb-6'}
                  data-testid={'timer-details-mobile'}>
                  <TimerDetails timingProvider={timerProps.timingProvider} />
                </Div>
              )}
              <Div onMouseEnter={onFormEngage} onTouchStart={onFormEngage}>
                <ContactTimerForm />
              </Div>
            </Flex>
          </Div>
        </Flex>
      </Suspense>
    </Sheet>
  );
};

export default ContactTimerSheet;
